import BoardDetailComment, {
  BoardDetailCommentRef,
} from '@/components/Board/BoardDetail/Feedback/BoardDetailComment';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { RecordFilter } from '@/types/api';
import useLayoutSize from '@/hooks/useLayoutSize';
import { LayoutType } from '@/constant/layout';
import MentionPermissionModal, {
  MentionPermissionModalRef,
} from '@/components/Record/MentionPermissionModal';
import { useTalkFlowShareList } from '@/hooks/apiHooks';
import useAi from '@/components/Record/hooks/useAi';

const useTalkFlowComment = () => {
  const { canModify } = useAi();
  const { isMobile } = useLayoutSize(LayoutType.DEFAULT);
  const { watch, getValues } = useFormContext<RecordFilter>();
  const [commentCount, setCommentCount] = useState(0);
  const boardDetailCommentRef = useRef<BoardDetailCommentRef | null>(null);
  const commentGroupId = getValues('commentGroupId');
  const docId = watch('docId');

  const { data: shareList, mutate: updateShareList } =
    useTalkFlowShareList(docId);

  const addComment = useCallback(async () => {
    await boardDetailCommentRef?.current?.submit?.();
  }, []);

  const mentionPermissionModalRef = useRef<MentionPermissionModalRef>(null);

  const passportIdsWithPermission = useMemo(() => {
    if (!shareList?.data) {
      return [];
    }

    // 공유 목록에 detailInfo 가 null 인 항목이 섞여올 수 있어, 권한 주체 식별은
    // scope.value(passportId) 로 폴백한다. (useAi, ShareSearch 와 동일한 방식)
    return shareList.data
      .map((data) => data.detailInfo?.passportId ?? data.scope?.value)
      .filter((passportId): passportId is string => Boolean(passportId));
  }, [shareList?.data]);

  const handleBeforeSubmit = useCallback(
    async (mentionIds: string[]) => {
      // 1. 모든 사람이 조회 또는 읽기 권한 있는지 확인
      const withoutPermission = mentionIds.filter(
        (id) => !passportIdsWithPermission.includes(id),
      );
      if (withoutPermission.length === 0) {
        return true;
      }

      // 2. 조회 권한 없으면 팝업 표시
      mentionPermissionModalRef.current?.open?.(withoutPermission);
      return false;
    },
    [passportIdsWithPermission],
  );

  const checkIsMentionText = useCallback(
    (id: string) => !passportIdsWithPermission.includes(id),
    [passportIdsWithPermission],
  );

  return {
    component: (
      <>
        <BoardDetailComment
          ref={boardDetailCommentRef}
          commentGroupId={commentGroupId}
          disabled={false}
          inputStyle={isMobile ? 'mobile' : 'top'}
          onChangeCount={setCommentCount}
          service={'talkFlow'}
          beforeSubmit={handleBeforeSubmit}
          checkIsText={checkIsMentionText}
          sx={{
            '&.mobile-style': {
              padding: '0 20px 24px',
              marginBottom: '56px',
            },

            padding: '0 24px 32px',
          }}
        />
        <MentionPermissionModal
          ref={mentionPermissionModalRef}
          docId={docId}
          hasSharePermission={canModify}
          shareList={shareList?.data || []}
          addComment={addComment}
          addCommentOnSuccess={async () => await updateShareList()}
        />
      </>
    ),
    commentCount,
  };
};

export default useTalkFlowComment;
